// mcc/reducer.js
import constants from "../../commonUtils/constants";
import * as actionTypes from "./actionTypes";

const initialState = {
    loading: false,
    filters: { keyword: "", status: "" },
    multiChannelCampaignsList: constants.extraction.listInitialState,
    multiChannelCampaign: constants.extraction.initialState.mcc,
    openModal: false,
    errors: {},
    errorMessage: "",
    acefoneLeadLists: [],
    // IDs that should begin polling immediately (set on trigger success,
    // consumed and cleared by the context's useEffect)
    pendingPollingIds: [],
};

const MultiChannelCampaigns = (state = initialState, action) => {
    switch (action.type) {

        // ── UI ─────────────────────────────────────────────────────────────
        case actionTypes.SET_LOADING:
            return { ...state, loading: action.payload };

        case actionTypes.SET_ERRORS:
            return { ...state, errors: action.payload };

        case actionTypes.SET_FILTERS:
            return { ...state, filters: action.payload };

        case actionTypes.SET_OPEN_MODAL:
            return { ...state, openModal: action.payload };

        case actionTypes.SET_MULTICHANNEL_CAMPAIGN:
            return { ...state, multiChannelCampaign: action.payload };

        // ── List ───────────────────────────────────────────────────────────
        case actionTypes.GET_MULTICHANNEL_CAMPAIGNS_SUCCESS:
            return {
                ...state,
                loading: false,
                multiChannelCampaignsList: action.payload,
                errors: {},
                errorMessage: "",
            };

        case actionTypes.GET_MULTICHANNEL_CAMPAIGNS_FAILURE:
            return {
                ...state,
                loading: false,
                errors: action.payload.data || {},
                errorMessage: action.payload.message || "Failed to fetch multichannel campaigns",
            };

        // ── Single ─────────────────────────────────────────────────────────
        case actionTypes.GET_MULTICHANNEL_CAMPAIGN_SUCCESS:
            return {
                ...state,
                loading: false,
                multiChannelCampaign: action.payload,
                openModal: true,
                errors: {},
                errorMessage: "",
            };

        case actionTypes.GET_MULTICHANNEL_CAMPAIGN_FAILURE:
            return {
                ...state,
                loading: false,
                openModal: false,
                errors: action.payload.data || {},
                errorMessage: action.payload.message || "Failed to fetch multichannel campaign",
            };

        // ── Create ─────────────────────────────────────────────────────────
        case actionTypes.CREATE_MULTICHANNEL_CAMPAIGN_SUCCESS:
            return {
                ...state,
                loading: false,
                openModal: true,
                multiChannelCampaign: action.payload,
                errors: {},
                errorMessage: "",
            };

        case actionTypes.CREATE_MULTICHANNEL_CAMPAIGN_FAILURE:
            return {
                ...state,
                loading: false,
                errors: action.payload.errors || {},
                errorMessage: action.payload.message || "Failed to create multichannel campaign",
            };

        // ── Update ─────────────────────────────────────────────────────────
        case actionTypes.UPDATE_MULTICHANNEL_CAMPAIGN_SUCCESS:
            return {
                ...state,
                loading: false,
                openModal: true,
                multiChannelCampaign: action.payload,
                errors: {},
                errorMessage: "",
            };

        case actionTypes.UPDATE_MULTICHANNEL_CAMPAIGN_FAILURE:
            return {
                ...state,
                loading: false,
                errors: action.payload.errors || {},
                errorMessage: action.payload.message || "Failed to update multichannel campaign",
            };

        // ── Trigger ────────────────────────────────────────────────────────
        case actionTypes.TRIGGER_CAMPAIGN_PROCESS_SUCCESS:
            return {
                ...state,
                loading: false,
                openModal: false,
                multiChannelCampaign: action.payload,
                errors: {},
                errorMessage: "",
            };

        case actionTypes.TRIGGER_CAMPAIGN_PROCESS_FAILURE:
            return {
                ...state,
                loading: false,
                errors: action.payload.errors || {},
                errorMessage: action.payload.message || "Failed to trigger campaign",
            };

        // ── Polling control ────────────────────────────────────────────────
        // Signals the context to start polling for this campaign immediately.
        // The context reads pendingPollingIds, calls startPolling(), then
        // dispatches CLEAR_PENDING_POLLING_IDS to reset this list.
        case actionTypes.START_CAMPAIGN_POLLING:
            return {
                ...state,
                pendingPollingIds: [
                    ...state.pendingPollingIds,
                    action.payload.campaignId,
                ],
            };

        case actionTypes.CLEAR_PENDING_POLLING_IDS:
            return { ...state, pendingPollingIds: [] };

        // ── Delete ─────────────────────────────────────────────────────────
        case actionTypes.DELETE_MULTICHANNEL_CAMPAIGN_SUCCESS:
            return {
                ...state,
                loading: false,
                errors: {},
                errorMessage: "",
            };

        case actionTypes.DELETE_MULTICHANNEL_CAMPAIGN_FAILURE:
            return {
                ...state,
                loading: false,
                errors: action.payload.data || {},
                errorMessage: action.payload.message || "Failed to delete multichannel campaign",
            };

        // ── Acefone lead lists ─────────────────────────────────────────────
        case actionTypes.FETCH_ACEFONE_LEAD_LISTS_SUCCESS:
            return { ...state, acefoneLeadLists: action.payload };

        case actionTypes.FETCH_ACEFONE_LEAD_LISTS_FAILURE:
            return {
                ...state,
                acefoneLeadLists: [],
                errors: action.payload.data || {},
                errorMessage: action.payload.message || "Failed to fetch Acefone lead lists",
            };

        // ── Retry campaign output (polling tick) ───────────────────────────
        // Patch the specific campaign's status in the list in-place so the
        // status badge updates immediately without a full list reload.
        case actionTypes.RETRY_CAMPAIGN_OUTPUT_SUCCESS: {
            const updated = action.payload; // { campaign, status }
            const updatedList = state.multiChannelCampaignsList?.data?.map(c =>
                c._id === updated.campaign._id ? { ...c, ...updated.campaign } : c
            ) || [];
            return {
                ...state,
                multiChannelCampaignsList: {
                    ...state.multiChannelCampaignsList,
                    data: updatedList,
                },
            };
        }

        case actionTypes.RETRY_CAMPAIGN_OUTPUT_FAILURE:
            // Silent — transient polling failures should not disrupt the UI
            return state;

        default:
            return state;
    }
};

export default MultiChannelCampaigns;